
import { Organization } from '@/models/Organization';
import {Component, Prop, Vue} from 'vue-property-decorator';
import { RideitNotice, NoticePriority } from '@/models/Rideit/RideitNotice';
import DateTimePicker from '@/components/DatetimePicker.vue';
import DestinationMultiSelect from '@/components/DestinationMultiSelect.vue';
import PassengerGroupMultiSelect from '@/components/PassengerGroupMultiSelect.vue';
import { RideitNoticeFormData, SELECT_ALL_VALUE } from '@/models/Rideit/RideitNoticeFormData';
import DateHelper, { NullableDateValue } from '@/util/DateHelper';

@Component({
  components: {
    DestinationMultiSelect,
    PassengerGroupMultiSelect,
    DateTimePicker,
  }
})
export default class RideitNoticeForm extends Vue {
  formRef = 'noticeForm' as const;
  organization: Organization = this.getOrganization();
  formData: RideitNoticeFormData = new RideitNoticeFormData();
  selectAllValue = SELECT_ALL_VALUE;
  dateHelper = new DateHelper();

  rules = {
    required: (val: any) => Boolean(val) || this.$t('validation.rules.required', 'Required'),
    maxLength: (max: number) => (val: string) => {
      if (!val) {
        return true;
      }
      return val.length <= max || this.$t('validation.rules.maxLength', { max });
    },
    requiredUnlessOtherTargets: (otherArrays: any[][]) => (val: any[]) => {
      const hasValue = Boolean(val.length);
      if (hasValue) {
        return true;
      }
      const otherHasValue = otherArrays.some((array) => Boolean(array.length));
      return otherHasValue || this.$t('validation.rules.required', 'Required');
    },
    isValidUrl: (val: string) => {
      if (!val) {
        return true;
      }
      try {
        new URL(val);
        return true;
      } catch {
        return this.$t('validation.rules.invalidUrl', 'Invalid URL');
      }
    },
  };

  @Prop({ required: true }) private notice: RideitNotice;
  @Prop({ default: false }) private loading: boolean;

  get isEditing() {
    return !!this.notice.id;
  }

  get validIndefinitely() {
    return !this.formData.validUntil;
  }

  set validIndefinitely(newValue: boolean) {
    if (newValue) {
      this.formData.validUntil = null;
    } 
  }

  get pinToTop() {
    return this.formData.priority === NoticePriority.Sticky;
  }

  set pinToTop(newValue: boolean) {
    this.formData.priority = newValue ? NoticePriority.Sticky : NoticePriority.Info;
  }

  get formElement() {
    return this.$refs[this.formRef] as Vue & { validate: () => boolean, resetValidation: () => void, reset: () => void };
  }

  get isDisabled() {
    return this.loading;
  }

  get validFromDate() {
    if (!this.formData.validFrom) {
      return null;
    }
    return this.dateHelper.dateYMD(this.formData.validFrom as string | Date);
  }

  set validFromDate(newValue: NullableDateValue) {
    this.formData.validFrom = newValue;
  }

  get validUntilDate() {
    if (!this.formData.validUntil) {
      return null;
    }
    return this.dateHelper.dateYMD(this.formData.validUntil as string | Date);
  }

  set validUntilDate(newValue: NullableDateValue) {
    this.formData.validUntil = newValue;
  }


  getOrganization() {
    const organizationId = this.$route.params.organizationId;
    return this.$store.state.organization.organizations.find((o: Organization) => o.id === organizationId);
  }

  cancel() {
    this.$emit('cancel');
  }

  save() {
    if (!this.formElement.validate()) {
      return;
    }
    this.$emit('save', this.formData.toNotice());
  }

  deleteNotice() {
    this.$emit('delete', this.notice);
  }

  mounted() {
    this.formData = new RideitNoticeFormData(this.notice, this.organization.id);
  }


}
